import { useMemo, useState } from 'react';
import { CallObjectUsage } from '@weave/schema-gen-ts/dist/schemas/phone/callroute/v1/callroute_service.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { useQuery } from 'react-query';
import { VoicemailConfigAPI } from '@frontend/api-voicemail-boxes';
import { hasSchemaACL } from '@frontend/auth-helpers';
import { EditableText } from '@frontend/editable-text';
import { useTranslation } from '@frontend/i18n';
import { SettingsTenantLocation, usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { useAppScopeStore } from '@frontend/scope';
import { useNavigationBlocker, useSettingsNavigate, useSettingsMatch } from '@frontend/settings-routing';
import { SkeletonLoader, useForm, useModalControl, useAlert, Chip } from '@frontend/design-system';
import { PhoneSettingsPage } from '../../components/settings/settings-page';
import { DeleteVoicemailBoxButton } from '../../components/voicemail-box/new/delete-button';
import { populateSchema, createUpdatePayload } from '../../components/voicemail-box/new/schema';
import type { CombinedData, GroupedCallObjectUsage, VoicemailGreeting } from '../../components/voicemail-box/new/types';
import {
  useVoicemailBoxMutations,
  useVoicemailBoxGreetingMutations,
} from '../../components/voicemail-box/new/use-voicemail-box-mutations';
import { VoicemailBoxContent } from '../../components/voicemail-box/new/voicemail-box-detail';
import { queryKeys } from '../../query-keys';
import { CancelEditModal } from '../../utils/cancel-edit.modal';
import { logError } from '../../utils/loggers';
import { buildVoicemailGreetingPath } from '../../utils/media-path';

const groupUsageByGreetingNumber = (callObjectUsage: CallObjectUsage[]): GroupedCallObjectUsage => {
  return callObjectUsage?.reduce((acc, usage) => {
    const greetingNumber = usage.instruction?.voicemailGreetingNumber;
    if (greetingNumber) {
      if (!acc[greetingNumber]) {
        acc[greetingNumber] = [];
      }
      acc[greetingNumber].push(usage);
    }
    return acc;
  }, {} as Record<number, CallObjectUsage[]>);
};

export const VoicemailDetail = () => {
  const { t } = useTranslation('phone');

  const { settingsTenantLocation } = usePhoneSettingsShallowStore('settingsTenantLocation');
  const { params, search } = useSettingsMatch<{
    Params: { id: string };
    Search: { crid?: string; crn?: string; nid?: string };
  }>();
  const { navigate } = useSettingsNavigate();

  // crid, crn and nid are set in the search params when the user is redirected from the call route details page.
  const [callRouteId, callRouteName, nodeId] = [search.crid, search.crn, search.nid];

  const { data: boxData, isLoading: isBoxDataLoading } = useQuery({
    queryKey: [settingsTenantLocation?.phoneTenantId, ...queryKeys.settings.readVoicemailBox(params.id)],
    queryFn: () => VoicemailConfigAPI.Read({ voicemailBoxId: params.id }),
    staleTime: 5 * 60 * 1000,
    enabled: !!params.id,
    meta: {
      error: {
        message: 'VoicemailDetail:voicemailBoxQuery | Failed to fetch voicemail box details',
        data: { voicemailBoxId: params.id, tenantLocationId: settingsTenantLocation?.id },
      },
    },
  });

  const { data: greetingData, isLoading: isGreetingDataLoading } = useQuery({
    queryKey: [settingsTenantLocation?.phoneTenantId, ...queryKeys.settings.listVoicemailBoxGreetings(params.id)],
    queryFn: () => VoicemailConfigAPI.ListGreetings({ mailboxId: params.id }),
    enabled: !!params.id,
    select: (data) => {
      return {
        greetings:
          data.greetings?.map((greeting) => ({
            ...greeting,
            mediaPath: buildVoicemailGreetingPath(greeting.greetingId),
          })) ?? [],
        callObjectUsage: groupUsageByGreetingNumber(data.callObjectUsage ?? []),
      };
    },
    meta: {
      error: {
        message: 'VoicemailDetail:greetingsQuery | Failed to fetch voicemail greetings',
        data: { voicemailBoxId: params.id, tenantLocationId: settingsTenantLocation?.id },
      },
    },
  });

  const data =
    boxData && greetingData
      ? { ...boxData.voicemailbox, greetings: greetingData.greetings, callObjectUsage: greetingData.callObjectUsage }
      : undefined;

  if (data && settingsTenantLocation) {
    return (
      <VoicemailDetailWithData
        data={data}
        tenantLocation={settingsTenantLocation}
        callRoute={{ id: callRouteId, name: callRouteName, nodeId }}
      />
    );
  }

  return (
    <PhoneSettingsPage
      renderContainer={false}
      title={data?.mailbox.name ?? ''}
      subtitle={t('Choose a ring option, assign devices, and customize the queue experience for callers on hold.')}
      breadcrumbs={
        callRouteId
          ? [
              { label: t('Phone'), to: '/phone/main' },
              { label: t('Call Routes'), to: '/phone/call-routes' },
              {
                label: callRouteName ?? '',
                onClick: () =>
                  navigate({
                    to: '/phone/call-routes/:id',
                    params: { id: callRouteId },
                    context: { contextNodeId: nodeId },
                  }),
              },
              { label: data?.mailbox.name ?? '', to: '/phone/voicemail-box/:id', params: { id: params.id } },
            ]
          : [
              { label: t('Phone'), to: '/phone/main' },
              { label: t('Voicemail Boxes'), to: '/phone/voicemail-box' },
              { label: data?.mailbox.name ?? '', to: '/phone/voicemail-box/:id', params: { id: params.id } },
            ]
      }
    >
      {isBoxDataLoading || isGreetingDataLoading ? (
        <SkeletonLoader count={2} height={300} distance={10} />
      ) : (
        t('No Voicemail Box found')
      )}
    </PhoneSettingsPage>
  );
};

const VoicemailDetailWithData = ({
  data,
  tenantLocation,
  callRoute,
}: {
  data: CombinedData;
  tenantLocation: SettingsTenantLocation;
  callRoute: { id?: string; name?: string; nodeId?: string };
}) => {
  const { t } = useTranslation('phone');
  const permissionBase = tenantLocation.id;
  const hasPhoneFullAccess = permissionBase ? hasSchemaACL(permissionBase, Permission.PHONE_FULL_ACCESS) : false;
  const { navigate } = useSettingsNavigate();
  const { getScopeName } = useAppScopeStore();

  const populatedSchema = useMemo(() => {
    return populateSchema(data);
  }, [data]);

  const form = useForm({
    fields: populatedSchema,
    computeChangedValues: true,
  });

  const field = form.getFieldProps('name');

  const { modalProps: cancelModalProps, triggerProps: cancelTriggerProps } = useModalControl();
  const [isGreetingModified, setIsGreetingModified] = useState(false);
  const [currentGreetings, setCurrentGreetings] = useState<VoicemailGreeting[]>(data.greetings);

  const { update } = useVoicemailBoxMutations(tenantLocation.phoneTenantId ?? '');
  const { bulkUpdate: updateGreetings } = useVoicemailBoxGreetingMutations(
    tenantLocation.phoneTenantId ?? '',
    data.mailbox.id
  );

  const alerts = useAlert();

  const hasUnsavedChanges = useMemo(() => {
    const hasFormChanges = Boolean(form.changedValues && Object.keys(form.changedValues).length > 0);
    return hasFormChanges || isGreetingModified;
  }, [form.changedValues, isGreetingModified]);

  const { proceed } = useNavigationBlocker(
    {
      shouldBlockFn: () => {
        if (hasUnsavedChanges || cancelModalProps.show) {
          cancelTriggerProps.onClick();
          return true;
        }
        return false;
      },
      enableBeforeUnload: true,
    },
    [hasUnsavedChanges, cancelModalProps.show]
  );

  const handleSave = async () => {
    const finalState = createUpdatePayload({
      state: data,
      formValues: form.values,
    });

    const promises = [];
    if (form.changedValues) {
      promises.push(update.mutateAsync(finalState));
    }

    if (isGreetingModified) {
      promises.push(
        updateGreetings.mutateAsync({
          mailboxId: data.mailbox.id,
          updateGreetings: currentGreetings,
        })
      );
    }

    try {
      await Promise.all(promises);

      alerts.success(t('Voicemail Box updated successfully.'));

      // Reset form state
      if (form.changedValues) {
        const newFields = populateSchema({
          ...data,
          extension:
            finalState.extensionNumber !== undefined
              ? {
                  id: data.extension?.id ?? '',
                  number: finalState.extensionNumber,
                }
              : undefined,
          locationIds: finalState.locationIds,
          mailbox: {
            id: finalState.voicemailBoxId,
            number: finalState.number ?? 0,
            tenantId: tenantLocation.phoneTenantId ?? '',
            name: finalState.name ?? '',
            email: finalState.emailAddresses ? finalState.emailAddresses.join(',') : '',
            pin: finalState.pin !== undefined ? finalState.pin : undefined,
            playMessageDate: finalState.playMessageDate,
            sendNotification: finalState.sendNotification,
            sharedAccess: finalState.sharedAccess,
            sms: finalState.smsNumbers ? finalState.smsNumbers.join(',') : '',
          },
        });

        form.reset(newFields);
      }

      if (isGreetingModified) {
        setIsGreetingModified(false);
      }
    } catch (error) {
      alerts.error(t('System failed to update Voicemail Box. Please try again.'));
      logError('VoicemailDetail:handleSave | Failed to save voicemail box details', {
        data: { voicemailBoxId: data.mailbox.id, tenantLocationId: tenantLocation.id, error },
      });
      throw error;
    }
  };

  const handleSaveAndProceed = async () => {
    try {
      await handleSave();
      proceed();
    } catch (error) {
      console.error('Save failed:', error);
    }
  };

  return (
    <PhoneSettingsPage
      css={{ marginBottom: 0 }}
      renderContainer={false}
      title={
        <div css={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <EditableText alwaysShowIcon isTitle fieldProps={field} />
          {data?.locationIds?.length === 1 && (
            <Chip.SingleChip>{getScopeName(data.locationIds[0] ?? '')}</Chip.SingleChip>
          )}
          {(data?.locationIds?.length ?? 0) > 1 && (
            <Chip.MultiChip>{`${data.locationIds?.length} locations`}</Chip.MultiChip>
          )}
        </div>
      }
      action={
        data && tenantLocation.phoneTenantId && hasPhoneFullAccess ? (
          <DeleteVoicemailBoxButton
            disabled={!data.isDeletable}
            box={data}
            phoneTenantId={tenantLocation.phoneTenantId}
          />
        ) : null
      }
      breadcrumbs={
        callRoute.id
          ? [
              { label: t('Phone'), to: '/phone/main' },
              { label: t('Call Routes'), to: '/phone/call-routes' },
              {
                label: callRoute.name ?? '',
                onClick: () =>
                  navigate({
                    to: '/phone/call-routes/:id',
                    params: { id: callRoute.id ?? '' },
                    context: { contextNodeId: callRoute.nodeId },
                  }),
              },
              { label: data?.mailbox.name ?? '', to: '/phone/voicemail-box/:id', params: { id: data.mailbox.id } },
            ]
          : [
              { label: t('Phone'), to: '/phone/main' },
              { label: t('Voicemail Boxes'), to: '/phone/voicemail-box' },
              { label: data?.mailbox.name ?? '', to: '/phone/voicemail-box/:id', params: { id: data.mailbox.id } },
            ]
      }
    >
      <VoicemailBoxContent
        data={data}
        tenantLocation={tenantLocation}
        form={form}
        hasPhoneFullAccess={hasPhoneFullAccess}
        onSave={handleSave}
        onGreetingModifiedChange={setIsGreetingModified}
        onGreetingsChange={setCurrentGreetings}
      />

      <CancelEditModal modalProps={cancelModalProps} onQuitClick={proceed} onSaveAndQuitClick={handleSaveAndProceed} />
    </PhoneSettingsPage>
  );
};
